/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Prediction Service - API client for SAT score predictions
 *
 * Provides methods for interacting with the score prediction API.
 * Used in conjunction with usePredictedScore hook for React integration.
 */

import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('predictionService')

/**
 * Supported strategy types for score prediction
 */
export const PREDICTION_STRATEGIES = [
  'SAT_MATH',
  'SAT_MATH_MINI',
  'SAT_READING_WRITING',
  'SAT_READING_WRITING_MINI'
]

/**
 * SAT Score Prediction Service
 * Handles all API calls related to predicted SAT scores
 */
class PredictionService {
  /**
   * Get combined SAT score prediction (Math + R/W + Total)
   * @returns {Promise<Object>} Combined prediction with sections
   */
  async getCombinedPrediction() {
    try {
      const response = await api.get('/predictions/combined')
      return response.data
    } catch (error) {
      logger.error('Error fetching combined prediction:', error)
      throw error
    }
  }

  /**
   * Get prediction for a specific strategy/section
   * @param {string} strategyType - 'SAT_MATH', 'SAT_READING_WRITING', etc.
   * @returns {Promise<Object>} Section prediction
   */
  async getStrategyPrediction(strategyType) {
    try {
      const response = await api.get(`/predictions/strategy/${strategyType}`)
      return response.data
    } catch (error) {
      logger.error(`Error fetching prediction for ${strategyType}:`, error)
      throw error
    }
  }

  /**
   * Save current prediction to history
   * @param {string} strategyType - Strategy type to save
   * @param {string} triggeredBy - What triggered the save ('manual_request', 'challenge_complete', etc.)
   * @param {string} challengeId - Optional challenge ID if triggered by challenge
   * @returns {Promise<Object>} Saved prediction record with change info
   */
  async savePrediction(strategyType, triggeredBy = 'manual_request', challengeId = null) {
    try {
      const response = await api.post(`/predictions/strategy/${strategyType}/save`, {
        triggeredBy,
        challengeId
      })
      return response.data
    } catch (error) {
      logger.error(`Error saving prediction for ${strategyType}:`, error)
      throw error
    }
  }

  /**
   * Get prediction history with optional filters
   * @param {Object} options - Query options
   * @param {string} options.strategyType - Filter by strategy
   * @param {number} options.limit - Max records to return (default: 20)
   * @param {number} options.offset - Pagination offset
   * @returns {Promise<Object>} History with predictions array and pagination
   */
  async getHistory(options = {}) {
    try {
      const params = new URLSearchParams()

      if (options.strategyType) params.append('strategyType', options.strategyType)
      if (options.limit) params.append('limit', options.limit.toString())
      if (options.offset) params.append('offset', options.offset.toString())

      const queryString = params.toString()
      const url = `/predictions/history${queryString ? `?${queryString}` : ''}`

      const response = await api.get(url)
      return response.data
    } catch (error) {
      logger.error('Error fetching prediction history:', error)
      throw error
    }
  }

  /**
   * Get most recent predictions for all supported strategies
   * @returns {Promise<Object>} Latest predictions keyed by strategy type
   */
  async getLatest() {
    try {
      const response = await api.get('/predictions/latest')
      return response.data
    } catch (error) {
      logger.error('Error fetching latest predictions:', error)
      throw error
    }
  }

  /**
   * Get list of supported strategy types for predictions
   * @returns {Promise<Array>} Array of supported strategy types
   */
  async getSupportedStrategies() {
    try {
      const response = await api.get('/predictions/supported-strategies')
      return response.data
    } catch (error) {
      logger.error('Error fetching supported strategies:', error)
      throw error
    }
  }

  // =========================================================================
  // Utility Methods
  // =========================================================================

  /**
   * Get human-readable strategy name
   * @param {string} strategyType - Strategy type constant
   * @returns {string} Display name
   */
  getStrategyDisplayName(strategyType) {
    const nameMap = {
      SAT_MATH: 'SAT Math',
      SAT_MATH_MINI: 'SAT Math Mini',
      SAT_READING_WRITING: 'SAT Reading & Writing',
      SAT_READING_WRITING_MINI: 'SAT R&W Mini'
    }
    return nameMap[strategyType] || strategyType
  }

  /**
   * Get section name from strategy type
   * @param {string} strategyType - Strategy type constant
   * @returns {string} Section name ('math' or 'reading_writing')
   */
  getSection(strategyType) {
    if (strategyType?.includes('MATH')) return 'math'
    if (strategyType?.includes('READING') || strategyType?.includes('WRITING'))
      return 'reading_writing'
    return null
  }

  /**
   * Get color class based on score tier
   * @param {number} score - Predicted score (200-800)
   * @param {number} maxScore - Maximum possible score (800 for sections, 1600 for total)
   * @returns {string} Tailwind color class
   */
  getScoreColorClass(score, maxScore = 800) {
    if (!score) return 'text-gray-500'

    const percentage = (score / maxScore) * 100

    if (percentage >= 75) return 'text-green-600' // Excellent
    if (percentage >= 50) return 'text-blue-600' // Good
    if (percentage >= 25) return 'text-yellow-600' // Fair
    return 'text-gray-600' // Needs work
  }

  /**
   * Get background color class for confidence level
   * @param {string} confidenceLevel - 'High', 'Good', 'Moderate', 'Low', 'Insufficient'
   * @returns {string} Tailwind background class
   */
  getConfidenceColorClass(confidenceLevel) {
    switch (confidenceLevel) {
      case 'High':
        return 'bg-green-100 text-green-800'
      case 'Good':
        return 'bg-blue-100 text-blue-800'
      case 'Moderate':
        return 'bg-yellow-100 text-yellow-800'
      case 'Low':
        return 'bg-orange-100 text-orange-800'
      case 'Insufficient':
        return 'bg-gray-100 text-gray-600'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  /**
   * Get trend icon and color
   * @param {string} trend - 'improving', 'declining', 'stable', etc.
   * @returns {Object} { icon: string, colorClass: string, label: string }
   */
  getTrendDisplay(trend) {
    switch (trend) {
      case 'improving':
        return { icon: '↑', colorClass: 'text-green-600', label: 'Improving' }
      case 'declining':
        return { icon: '↓', colorClass: 'text-red-600', label: 'Declining' }
      case 'stable':
        return { icon: '→', colorClass: 'text-gray-500', label: 'Stable' }
      case 'insufficient_data':
        return { icon: '?', colorClass: 'text-gray-400', label: 'Not enough data' }
      default:
        return { icon: '-', colorClass: 'text-gray-400', label: 'Unknown' }
    }
  }

  /**
   * Format score for display
   * @param {number} score - Score value
   * @param {boolean} showMax - Whether to show max score
   * @param {number} maxScore - Maximum score value
   * @returns {string} Formatted score string
   */
  formatScore(score, showMax = false, maxScore = 800) {
    if (!score && score !== 0) return '--'
    if (showMax) return `${score}/${maxScore}`
    return score.toString()
  }

  /**
   * Check if strategy supports SPR (Student-Produced Response) bonus
   * @param {string} strategyType - Strategy type
   * @returns {boolean} True if Math strategy
   */
  supportsSprBonus(strategyType) {
    return strategyType?.includes('MATH') || false
  }

  // =========================================================================
  // Per-Subject Score Methods (for Reflective Dashboard)
  // =========================================================================

  /**
   * Get current and predicted scores per subject (Math vs R&W)
   * Current = EMA-based "where you are now"
   * Predicted = trend-based "where you're heading"
   * @returns {Promise<Object>} Per-subject scores with trends
   */
  async getPerSubjectScores() {
    try {
      const response = await api.get('/predictions/current')
      return response.data
    } catch (error) {
      logger.error('Error fetching per-subject scores:', error)
      throw error
    }
  }

  /**
   * Get detailed trend analysis for a specific strategy
   * @param {string} strategyType - Strategy type
   * @param {number} windowDays - Time window in days (default: 30)
   * @returns {Promise<Object>} Trend analysis with history
   */
  async getTrendAnalysis(strategyType, windowDays = 30) {
    try {
      const response = await api.get(`/predictions/trend/${strategyType}?windowDays=${windowDays}`)
      return response.data
    } catch (error) {
      logger.error(`Error fetching trend for ${strategyType}:`, error)
      throw error
    }
  }

  /**
   * Get this week's study-plan adherence (actual vs recommended hours, behind/on_track/ahead)
   * @returns {Promise<Object>} Plan adherence payload
   */
  async getPlanAdherence() {
    try {
      const response = await api.get('/predictions/plan-adherence')
      return response.data
    } catch (error) {
      logger.error('Error fetching plan adherence:', error)
      throw error
    }
  }

  /**
   * Get data for progress share card generation
   * @returns {Promise<Object>} Share card data (scores, badges, levels)
   */
  async getShareCardData() {
    try {
      const response = await api.get('/predictions/share-data')
      return response.data
    } catch (error) {
      logger.error('Error fetching share card data:', error)
      throw error
    }
  }
}

// Export singleton instance
const predictionService = new PredictionService()
export default predictionService
